import React, { useEffect, useState } from "react"
import { SpinnerText } from "../../ui/Spinner"
import TaxonomyTree from "./TaxonomyTree"
import SearchBar from "./SearchBar"
import { getTaxonomy, getTermsByParentConcept } from "../../../services/euipoApi"

type GoodsEntry = { conceptId?: string; classNumber?: number; text: string }
type ApiTerm = GoodsEntry & { conceptId?: string }

export default function DatabaseList({
  terms,
  setTerms,
}: {
  terms: GoodsEntry[]
  setTerms: React.Dispatch<React.SetStateAction<GoodsEntry[]>>
}) {
  const [taxonomy, setTaxonomy] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [selectedClass, setSelectedClass] = useState<any>(null)
  const [classItems, setClassItems] = useState<ApiTerm[]>([])
  const [termsLoading, setTermsLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<ApiTerm[]>([])
  const [visibleCount, setVisibleCount] = useState(30)

  useEffect(() => {
    let active = true
    getTaxonomy()
      .then((result) => { if (active) setTaxonomy(result) })
      .catch((err) => console.error("Fehler beim Laden der Taxonomie:", err))
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [])

  async function handleSelectClass(node: any) {
    setSelectedClass(node)
    setTermsLoading(true)
    setClassItems([])
    setVisibleCount(30)
    try {
      const result = await getTermsByParentConcept(node.conceptId)
      setClassItems(result)
    } catch (err) {
      console.error("Fehler beim Laden der Begriffe:", err)
    } finally {
      setTermsLoading(false)
    }
  }

  const termKey = (term: GoodsEntry) =>
    term.conceptId ? `id:${term.conceptId}` : `text:${term.classNumber ?? ""}:${term.text.trim().toLowerCase()}`

  function toggleTerm(term: ApiTerm) {
    setTerms((prev) => {
      const exists = prev.some((t) => termKey(t) === termKey(term))
      const updated = exists
        ? prev.filter((t) => termKey(t) !== termKey(term))
        : [...prev, { conceptId: term.conceptId, classNumber: term.classNumber, text: term.text }]
      sessionStorage.setItem("checkedTerms", JSON.stringify(updated))
      return updated
    })
  }

  function handleClearList() {
    if (window.confirm("Clear your directory?")) {
      setTerms([])
      sessionStorage.removeItem("checkedTerms")
    }
  }

  const uniqueClasses = new Set(terms.map((t) => t.classNumber).filter((n) => n != null)).size
  const goods = taxonomy?.items?.find((i: any) => i.text === "Goods")?.items || []
  const services = taxonomy?.items?.find((i: any) => i.text === "Services")?.items || []

  function TermRows({ items }: { items: ApiTerm[] }) {
    return (
      <div className="divide-y divide-slate-100">
        {items.map((item, index) => {
          const checked = terms.some((t) => termKey(t) === termKey(item))
          return (
            <label key={`${termKey(item)}-${index}`} className={`flex cursor-pointer items-start gap-3 px-3 py-3 text-sm transition hover:bg-blue-50 ${checked ? "bg-blue-50/60" : "bg-white"}`}>
              <input type="checkbox" checked={checked} onChange={() => toggleTerm(item)} className="mt-0.5 h-4 w-4 shrink-0 accent-blue-700" />
              <span className="w-9 shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-center text-xs font-medium text-slate-600">{item.classNumber ?? "–"}</span>
              <span className="min-w-0 flex-1 leading-5 text-slate-800">{item.text}</span>
            </label>
          )
        })}
      </div>
    )
  }


  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-4 py-3 text-sm">
        <div className="flex gap-5 text-slate-700">
          <span><strong className="text-blue-800">{uniqueClasses}</strong> classes</span>
          <span><strong className="text-blue-800">{terms.length}</strong> terms</span>
        </div>
        <button type="button" onClick={handleClearList} disabled={!terms.length} className="font-medium text-slate-600 hover:text-red-600 disabled:opacity-40">Clear directory</button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-2 text-sm font-semibold text-blue-900">Search harmonized terms</h3>
        <SearchBar onResults={setSearchResults} />
        {searchResults.length > 0 && (
          <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200">
            <div className="sticky top-0 z-10 bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Search results · click a term to select</div>
            <TermRows items={searchResults} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
            <h3 className="text-sm font-semibold text-blue-900">Nice Classification</h3>
            <p className="mt-1 text-xs text-slate-500">Choose a category to explore terms</p>
          </div>
          <div className="max-h-[26rem] overflow-y-auto p-4 text-sm">
            {loading ? <p className="text-slate-500"><SpinnerText>Loading classes...</SpinnerText></p> : (
              <>
                <details open className="mb-3">
                  <summary className="cursor-pointer rounded-md bg-blue-50 px-3 py-2 font-semibold text-blue-800">Goods · Classes 1–34</summary>
                  <TaxonomyTree items={goods} onSelectClass={handleSelectClass} />
                </details>
                <details>
                  <summary className="cursor-pointer rounded-md bg-blue-50 px-3 py-2 font-semibold text-blue-800">Services · Classes 35–45</summary>
                  <TaxonomyTree items={services} onSelectClass={handleSelectClass} />
                </details>
              </>
            )}
          </div>
        </div>

        <div className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
            <h3 className="text-sm font-semibold text-blue-900">{selectedClass?.text ?? "Terms"}</h3>
            <p className="mt-1 text-xs text-slate-500">Select terms to add to your directory</p>
          </div>
          <div className="max-h-[26rem] overflow-y-auto">
            {!selectedClass ? <p className="p-4 text-sm text-slate-500">Select a class on the left to view its terms.</p>
              : termsLoading ? <p className="p-4 text-sm text-slate-500"><SpinnerText>Loading terms...</SpinnerText></p>
              : classItems.length === 0 ? <p className="p-4 text-sm text-slate-500">No terms found.</p>
              : <>
                  <TermRows items={classItems.slice(0, visibleCount)} />
                  {visibleCount < classItems.length && (
                    <button type="button" onClick={() => setVisibleCount((n) => n + 30)} className="w-full border-t border-slate-200 px-4 py-3 text-sm font-semibold text-blue-700 hover:bg-blue-50">Load more terms ({classItems.length - visibleCount} remaining)</button>
                  )}
                </>}
          </div>
        </div>
      </div>


    </div>
  )
}
